import React from 'react'
import { Link } from 'react-router-dom'
import { resolveGovernedActionRoute } from './processRouteMap'

const RESPONSIBILITY_LABELS = {
  MANAGEMENT_PLANNING: 'Management Planning',
  BOARD_GOVERNANCE: 'Board Governance',
  MANAGEMENT_EXECUTION: 'Management Execution',
  OUTCOME_GOVERNANCE: 'Outcome Governance',
}

const AUTHORITY_LABELS = { MANAGEMENT: 'Management authority', BOARD: 'Board authority' }

const CTA_LABELS = {
  SUBMIT_INVESTMENT_PROPOSAL: 'Open Proposal',
  RECORD_INITIAL_BOARD_DECISION: 'Open Board Review',
  MOBILIZE_INVESTMENT_PROGRAM: 'Open Board Decision',
  ACTIVATE_PROGRAM_PROJECT: 'Open Investment Program',
  PREPARE_PROGRAM_REAUTHORIZATION: 'Open Reauthorization Management',
  SUBMIT_PROGRAM_REAUTHORIZATION: 'Open Reauthorization Management',
  RECORD_PROGRAM_REAUTHORIZATION_DECISION: 'Open Reauthorization Review',
  APPLY_PROGRAM_REAUTHORIZATION: 'Open Reauthorization Review',
}

function Action({ action }) {
  const actionRoute = resolveGovernedActionRoute(action)
  const required = action.requirement === 'REQUIRED'
  return <li><article className={`rounded-lg border p-4 ${required ? 'border-amber-300 bg-amber-50/40' : 'border-slate-200 bg-white'}`} data-action-id={action.action_id}>
    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className={`rounded border px-2 py-0.5 font-bold uppercase tracking-wider ${required ? 'border-amber-400 text-amber-900' : 'border-slate-300 text-slate-600'}`}>{action.requirement}</span>
          <span className="font-semibold text-slate-700">{RESPONSIBILITY_LABELS[action.responsibility] || action.responsibility || 'Governance responsibility unavailable'}</span>
          {action.authority && <span className="text-slate-500">· {AUTHORITY_LABELS[action.authority] || action.authority}</span>}
        </div>
        <h4 className="mt-2 text-base font-bold text-slate-950">{action.label || 'Governed action'}</h4>
        <p className="mt-1 text-sm leading-6 text-slate-600">{action.rationale || 'Governance rationale is unavailable.'}</p>
      </div>
      {actionRoute ? <Link className="shrink-0 self-start rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-blue-700 hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600" to={actionRoute}>{CTA_LABELS[action.action_type]}</Link> : <span className="shrink-0 text-sm text-slate-500">Open governance context unavailable</span>}
    </div>
  </article></li>
}

function ActionGroup({ actions, heading, id }) {
  if (!actions.length) return null
  return <div><h3 className="text-sm font-bold uppercase tracking-wider text-slate-700" id={id}>{heading}</h3><ul aria-labelledby={id} className="mt-3 space-y-3">{actions.map((action, index) => <Action action={action} key={action.action_id || `${action.action_type}-${index}`} />)}</ul></div>
}

export default function GovernedActions({ actions = [], summary = {} }) {
  const required = actions.filter((action) => action.requirement === 'REQUIRED')
  const available = actions.filter((action) => action.requirement === 'AVAILABLE')
  const total = summary.total ?? actions.length
  return <section aria-labelledby="governed-actions-heading" className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
    <div className="flex flex-wrap items-baseline justify-between gap-2"><h2 className="text-xl font-bold text-slate-950" id="governed-actions-heading">Next Governed Actions</h2>{total > 0 && <p className="text-xs text-slate-500">{total} governed {total === 1 ? 'action' : 'actions'} · {summary.required ?? required.length} required · {summary.available ?? available.length} available</p>}</div>
    <p className="mt-1 text-sm text-slate-600">Governed actions currently supported by authoritative evidence across active Investment Governance contexts.</p>
    {!actions.length ? <p className="mt-4 text-sm text-slate-600">No governed actions are currently identified from the available authoritative evidence.</p> : <div className="mt-5 space-y-6"><ActionGroup actions={required} heading="Required Governance Actions" id="required-governance-actions" /><ActionGroup actions={available} heading="Available Governance Actions" id="available-governance-actions" /></div>}
  </section>
}
